
  import { Vue, Component, Prop } from "vue-property-decorator";

  import GET_PAYMENT_DETAILS from "@/apollo/payment/GetPaymentDetails.gql";

  import AuthCard from "@/components/auth/AuthCard.vue";
  import AuthFormLink from "@/components/auth/AuthFormLink.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import PriceCardViewPricing from "@/components/payment/prices/PriceCardViewPricing.vue";
  import PriceCardViewTitle from "@/components/payment/prices/PriceCardViewTitle.vue";
  import UpdateSubscriptionOptions from "@/components/payment/UpdateSubscriptionOptions.vue";

  import isPaymentCardExpired from "@/helpers/payment/isPaymentCardExpired";

  import { Company, Payment_Card, User } from "@/generated/graphql";
  import RouteNames from "@/entities/RouteNames";
  import { SubscriptionPlan } from "@/entities/payment/SubscriptionPlan";
  import PricePlanProvider from "@/components/payment/prices/PricePlanProvider.vue";
  import { SubscriptionPlanInnerName } from "@/entities/payment/InnerNames";
  import { Maybe } from "graphql/jsutils/Maybe";
  import CreateSubscriptionOptions from "@/components/payment/CreateSubscriptionOptions.vue";
  import lambdaRequest from "@/helpers/lambdaRequest";
  import UiButton from "@/components/button/ui-button.vue";
  import Processing from "@/components/common/Processing.vue";
  import Spinner from "@/components/common/Spinner.vue";
  import FormError from "@/components/common/FormError.vue";

  interface PaymentDetails {
    subscriptionPlan: SubscriptionPlan;
    paymentCards: Payment_Card[];
  }

  @Component({
    components: {
      FormError,
      Processing,
      UiButton,
      CreateSubscriptionOptions,
      AuthCard,
      AuthFormLink,
      PricePlanProvider,
      LoadingOverlay,
      PriceCardViewPricing,
      PriceCardViewTitle,
      UpdateSubscriptionOptions,
      Spinner,
    },
    apollo: {
      paymentDetails: {
        query: GET_PAYMENT_DETAILS,
        variables() {
          return {
            subscriptionPlanId: this.subscriptionPlanId,
            userId: this.currentUser.id,
          };
        },
        update: (data): PaymentDetails => {
          const subscriptionPlan = data.subscription_plan_by_pk;
          const paymentCards = data.user_by_pk.payment_cards;
          return {
            subscriptionPlan,
            paymentCards,
          };
        },
      },
    },
  })
  export default class PaymentCardAuthForm extends Vue {
    @Prop({type: Object, required: true}) readonly currentUser!: User;
    @Prop({type: Object, required: true}) readonly company!: Company;
    @Prop({type: String, required: true}) readonly subscriptionPlanId!: string;

    paymentDetails: PaymentDetails | null = null;
    processing: boolean = false;
    errorMessage: string = "";

    get isPaymentCardsExist(): boolean {
      return this.paymentCards.length > 0;
    }

    get firstSubscription(): boolean {
      return this.company.subscriptions_aggregate.aggregate!.count === 0;
    }

    get isCardsExpired(): boolean {
      return this.paymentCards.every(({exp_month, exp_year}) =>
        isPaymentCardExpired({expMonth: exp_month, expYear: exp_year})
      );
    }

    get cardsListTitle(): string {
      if (this.isCardsExpired) {
        return "You can't use previously added card";
      }
      return "You can use previously added card";
    }

    get cardFormTitle(): string {
      if (this.isCardsExpired) {
        return "Edit that card";
      }
      return "Or edit that existing card";
    }

    get managersCount(): number {
      return this.subscriptionPlan.subscription_plan_access_permission!.managers_count;
    }

    get authCardTitle(): string {
      if (this.paymentCards.length > 0) {
        return "";
      }
      return "Add credit card";
    }

    get subscriptionPlan(): SubscriptionPlan {
      return this.paymentDetails!.subscriptionPlan;
    }

    get paymentCards(): Payment_Card[] {
      return this.paymentDetails!.paymentCards;
    }

    get innerSubscriptionName(): Maybe<SubscriptionPlanInnerName> {
      return this.subscriptionPlan?.inner_name;
    }

    get isLoading(): boolean {
      return this.$apollo.queries.paymentDetails.loading || this.processing;
    }

    get isDataLoaded(): boolean {
      return Boolean(this.paymentDetails);
    }

    get inactiveSubscriptionsExist(): boolean {
      return this.company.subscriptions_aggregate!.aggregate!.count! > 0;
    }

    get isTrial(): boolean {
      return !this.inactiveSubscriptionsExist;
    }

    signOut(): void {
      // this.$router.push({
      //   name: RouteNames.COMPANY_DATABASE,
      // });
       window.location.href = "/company_database/contractors";
    }


    onCompleteSubscriptionAction(): void {
      this.processing = true;
      this.$emit("refetch");
      // this.$router.push({
      //   name: "company_database/contractors",
      // });
      window.location.href = "/company_database/contractors";
    }

    redirectToPricesPage(): void {
      this.$router.push({
        name: RouteNames.CHOOSE_AUTH_SUBSCRIPTIONS,
      });
    }

    async subscribeWithoutCard(): Promise<void> {
      if (this.processing) {
        return;
      }

      this.processing = true;

      try {
        await lambdaRequest({
          lambdaName: "subscribeWithoutCard",
          requestData: {
            subscription_plan_id: this.subscriptionPlanId,
          },
        });

        this.$emit("refetch");
      } catch (error) {
        this.errorMessage = error.message;
        this.processing = false;
      }
        window.location.href = "/company_database/contractors";
    }
  }
